import useReveal from "../hooks/useReveal";

export default function Solution() {
  const ref = useReveal();
  return (
    <section
      ref={ref}
      id="solutions"
      data-testid="solution-section"
      className="relative py-28 md:py-40 border-t border-[var(--border-faint)] overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.10] dark:opacity-[0.18] mix-blend-multiply dark:mix-blend-screen"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1772050138768-2107c6e62a03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdyZWVuJTIwbmV0d29yayUyMGxpbmVzJTIwZGFya3xlbnwwfHx8fDE3NzgxMjM0MjR8MA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16">
          <div className="md:col-span-7 reveal">
            <div className="eyebrow mb-4">— 04 / The SSPL Solution</div>
            <h2 className="font-display text-3xl md:text-5xl text-[var(--text)] leading-[1.05] tracking-tight">
              A full-stack supercomputing platform —{" "}
              <span className="text-[var(--color-primary)] dark:text-[var(--color-lime)]">
                HPC, AI, and Quantum
              </span>
              , unified.
            </h2>
          </div>
          <div className="md:col-span-5 reveal flex items-end">
            <p className="text-[var(--text-60)] text-[15px] leading-relaxed">
              Five integrated capability pillars that combine high-performance,
              artificial intelligence, and quantum compute into a single
              sovereign, ecosystem-extensible platform.
            </p>
          </div>
        </div>

        {/* Bento Row 1 — flagship + yellow + standard */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-5">
          {/* Big featured card — Sovereign Data Launchpad */}
          <div
            data-testid="solution-card-0"
            className="reveal md:col-span-4 md:row-span-2 border border-[var(--border-subtle)] p-8 md:p-12 relative overflow-hidden group hover:border-[var(--color-lime-deep)]/40 dark:hover:border-[var(--color-lime)]/40 transition-colors duration-500 min-h-[420px]"
            style={{ background: "var(--surface)" }}
          >
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[var(--color-primary)]/15 blur-3xl group-hover:bg-[var(--color-primary)]/30 transition-all duration-700" />
            <div className="relative">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--color-accent-text)] mb-6">
                Pillar — Flagship Framework
              </div>
              <h3 className="font-display text-[var(--text)] text-3xl md:text-5xl leading-[1.05] tracking-tight mb-6 max-w-xl">
                Sovereign Data Launchpad
                <span className="align-super text-base md:text-2xl ml-1">™</span>
              </h3>
              <p className="text-[var(--text-65)] text-[15px] leading-relaxed max-w-lg mb-10">
                The Sovereign Data Launchpad™ Model is a{" "}
                <span className="bg-[var(--color-yellow)]/40 dark:bg-[var(--color-yellow)]/30 text-[var(--text)] px-1">
                  world-first framework
                </span>{" "}
                that unlocks the regional opportunity — combining sovereign
                data residency, accelerator-class compute, and ecosystem
                interoperability into a single deployable model.
              </p>
            </div>
          </div>

          {/* Yellow accent — Quantum Computing */}
          <div
            data-testid="solution-card-1"
            className="reveal md:col-span-2 bg-[var(--color-yellow)] text-black p-8 relative overflow-hidden group min-h-[200px]"
          >
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-black/70 mb-6">
              Pillar — Foundation
            </div>
            <h3 className="font-display text-2xl md:text-3xl leading-tight tracking-tight mb-3">
              High Performance Computing
            </h3>
            <p className="text-black/80 text-sm leading-relaxed">
              Powerful parallel-processing systems performing complex
              calculations at extreme scale and speed — the foundation for
              the integrated heterogeneous architecture.
            </p>
            <div className="absolute bottom-3 right-3 font-display text-6xl md:text-7xl text-black/10 tracking-tighter">
              HPC
            </div>
          </div>

          {/* Standard — Integrated Heterogeneous Platform */}
          <div
            data-testid="solution-card-2"
            className="reveal md:col-span-2 border border-[var(--border-subtle)] p-8 hover:border-[var(--color-teal)]/50 transition-colors min-h-[200px] relative"
            style={{ background: "var(--surface)" }}
          >
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--color-teal)] mb-6">
              Pillar — Platform
            </div>
              <h3 className="font-display text-[var(--text)] text-2xl md:text-3xl leading-tight tracking-tight mb-3">
                Integrated Heterogeneous Platform
              </h3>
            <p className="text-[var(--text-60)] text-sm leading-relaxed">
              Ecosystem-integrated, accessible platform extension across
              multiple elements of the cloud ecosystem.
            </p>
            <div className="absolute bottom-4 right-5 font-mono text-[var(--color-teal)] text-xs tracking-[0.2em]">
              MULTI-CLOUD
            </div>
          </div>
        </div>

        {/* Bento Row 2 — HPC + AI */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mt-4 md:mt-5">
          {/* HPC */}
          <div
            data-testid="solution-card-3"
            className="reveal border border-[var(--border-subtle)] p-8 hover:border-[var(--color-primary)]/50 transition-colors min-h-[200px] relative group overflow-hidden"
            style={{ background: "var(--surface)" }}
          >
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-[var(--color-primary)]/10 blur-3xl group-hover:bg-[var(--color-primary)]/20 transition-all duration-700" />
            <div className="relative">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--color-accent-text)] mb-6">
                Pillar — Quantum
              </div>
              <h3 className="font-display text-[var(--text)] text-2xl md:text-3xl leading-tight tracking-tight mb-3">
                Quantum Computing
              </h3>
              <p className="text-[var(--text-60)] text-sm leading-relaxed max-w-md">
                Quantum Processing Units (QPUs) integrated into heterogeneous
                architectures — complementing classical compute, not replacing
                it.
              </p>
            </div>
            <div
              className="absolute bottom-3 right-3 font-display text-7xl md:text-8xl tracking-tighter pointer-events-none select-none leading-none"
              style={{ color: "var(--text)", opacity: 0.06 }}
            >
              QC
            </div>
          </div>

          {/* AI */}
          <div
            data-testid="solution-card-4"
            className="reveal border border-[var(--border-subtle)] p-8 hover:border-[var(--color-primary)]/50 transition-colors min-h-[200px] relative group overflow-hidden"
            style={{ background: "var(--surface)" }}
          >
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[var(--color-teal)]/10 blur-3xl group-hover:bg-[var(--color-teal)]/20 transition-all duration-700" />
            <div className="relative">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--color-teal)] mb-6">
                Pillar — Intelligence
              </div>
              <h3 className="font-display text-[var(--text)] text-2xl md:text-3xl leading-tight tracking-tight mb-3">
                Artificial Intelligence
              </h3>
              <p className="text-[var(--text-60)] text-sm leading-relaxed max-w-md">
                Built on the HPC foundation — enabling AI research, large-model
                training, and high-throughput inferencing at sovereign scale.
              </p>
            </div>
            <div
              className="absolute bottom-3 right-3 font-display text-7xl md:text-8xl tracking-tighter pointer-events-none select-none leading-none"
              style={{ color: "var(--text)", opacity: 0.06 }}
            >
              AI
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
